import * as React from "react";
import { Link } from "react-router-dom";
import { resolveInject } from "app/di";
import { IAppConfigProvider } from "app/services/config/app-config-loader.service";
import { ISparqlExecutor } from "app/services/infrastructure/sparql-executor";
import { NavigateService } from "app/services/navigate.service";
import {
    SearchEngine,
    ChangeEvent,
    ChangeListener,
    ResultData
} from "app/services/data-loader/search-engine.service";

import TextField from "@mui/material/TextField";
import Popper from "@mui/material/Popper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import SearchIcon from "@mui/icons-material/Search";

import "./search.less";
import { IconButton } from "@mui/material";

interface IComponentState {
    searchTerm: string;
    searchTermIsTooShort: boolean;
    searchResults?: ResultData;
    isSearchResultsOpen: boolean;
    isSearchInProgress: boolean;
    anchorEl: null | HTMLElement;
}

interface IComponentProps {
    quicksearch?: boolean;
}

interface MatchObject {
    iri: string;
    name: string;
    groupText: string;
}

export class Search
    extends React.Component<IComponentProps, IComponentState>
    implements ChangeListener
{
    private searchTimer: any;
    private inputRef = React.createRef<HTMLInputElement>();

    constructor(props: IComponentProps) {
        super(props);
        this.state = {
            searchTerm: "",
            searchTermIsTooShort: true,
            isSearchResultsOpen: false,
            isSearchInProgress: false,
            anchorEl: null
        };
    }

    async componentDidMount() {
        SearchEngine.addChangeListener(this);
    }

    async componentWillUnmount() {
        SearchEngine.removeChangeListener(this);
    }

    onChange(e: ChangeEvent) {
        this.setState({
            isSearchInProgress: false,
            searchResults: e.data
        });
    }

    performSearch = async (limitSearch: boolean = true) => {
        const searchTerm = this.state.searchTerm;

        if (searchTerm.length < 2) {
            this.setState({
                searchTermIsTooShort: true,
                isSearchResultsOpen: true
            });
            return;
        }

        this.setState({
            searchTermIsTooShort: false,
            isSearchInProgress: true,
            isSearchResultsOpen: true
        });

        const searchOptions = limitSearch ? { limit: SearchEngine.MAXCOUNT_QUICKSEARCH_ITEMS } : {};
        SearchEngine.search(searchTerm, searchOptions);
    };

    performUnlimitedSearch = async () => {
        const searchPath = NavigateService.search(this.state.searchTerm, { limit: 0 });
        window.location.hash = searchPath;
    };

    closeSearch = () => {
        this.setState({ isSearchResultsOpen: false });
    };

    handleMenuOpen = () => {
        this.setState({ isSearchResultsOpen: true });
    };

    handleMenuClose = () => {
        this.setState({ isSearchResultsOpen: false });
    };

    searchBtnClick = async () => {
        const searchKey = this.state.searchTerm;
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }

        if (this.isIriPattern(searchKey)) {
            NavigateService.browse(searchKey);
            return;
        }

        await this.performSearch();
    };

    isIriPattern(text: string): boolean {
        return text.startsWith("http://") || text.startsWith("https://");
    }

    searchInputChange(newValue: string) {
        this.setState({ searchTerm: newValue });
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }
        this.searchTimer = setTimeout(async () => {
            await this.performSearch();
        }, 500);
    }

    searchInputKeyDown(event: React.KeyboardEvent) {
        if (event.key === "Escape") {
            this.closeSearch();
        }
        if (event.key === "Enter") {
            this.searchBtnClick();
        }
        if (event.key === "ArrowDown") {
            this.setFocusToSearchResults();
        }
    }

    setFocusToSearchResults() {
        const searchResults = document.querySelector(".search-result-item") as HTMLElement;
        if (searchResults) {
            searchResults.focus();
        }
    }

    render(): React.JSX.Element {
        const { quicksearch } = this.props;
        const { searchTerm, isSearchResultsOpen } = this.state;
        return (
            <ClickAwayListener onClickAway={this.closeSearch}>
                <form
                    className="form-inline my-2 my-lg-0 search-component"
                    onSubmit={(e) => e.preventDefault()}
                >
                    <Paper
                        elevation={2}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "2px 8px",
                            width: 400
                        }}
                    >
                        <TextField
                            variant="standard"
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                this.searchInputChange(e.target.value)
                            }
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                                this.searchInputKeyDown(e)
                            }
                            slotProps={{ input: { disableUnderline: true } }}
                            style={{ flex: 1 }}
                            inputRef={this.inputRef}
                            size="small"
                            onFocus={quicksearch ? this.handleMenuOpen : undefined}
                        />
                        <IconButton
                            color="primary"
                            style={{ minWidth: 40, marginLeft: 8 }}
                            onClick={this.searchBtnClick}
                        >
                            <SearchIcon />
                        </IconButton>
                    </Paper>
                    {quicksearch && this.state.searchTerm && (
                        <Popper
                            open={isSearchResultsOpen}
                            anchorEl={this.inputRef.current}
                            placement="bottom-start"
                            style={{ zIndex: 1300, width: 400 }}
                        >
                            <div tabIndex={-1}>
                                <Paper elevation={3} style={{ width: 400, marginTop: 4 }}>
                                    {this.renderSearchResult()}
                                </Paper>
                            </div>
                        </Popper>
                    )}
                </form>
            </ClickAwayListener>
        );
    }

    getSearchMessage() {
        if (!this.state.searchTerm) {
            return "Please enter search term";
        }
        if (this.state.searchTermIsTooShort) {
            return "Search term is too short";
        }
        if (this.state.isSearchInProgress) {
            return "Searching...";
        }
        if (
            this.state.searchResults &&
            this.state.searchResults.matches &&
            this.state.searchResults.matches.length === 0
        ) {
            return "No results";
        }
        return null;
    }

    renderSearchResult() {
        const msg = this.getSearchMessage();
        if (msg) {
            return <MenuItem disabled>{msg}</MenuItem>;
        }

        const results = this.state.searchResults;
        if (results === undefined) {
            return null;
        }

        const matches = (results.matches || []).slice(
            0,
            results.limited ? SearchEngine.MAXCOUNT_QUICKSEARCH_ITEMS : undefined
        );
        const matchesPerGroup = SearchEngine.getMatchesPer("groupText", matches);

        return (
            <>
                <MenuItem>
                    <Typography variant="subtitle2">{results.message}</Typography>
                    {results.limited && (
                        <>
                            &nbsp;
                            <Typography
                                variant="caption"
                                color="primary"
                                style={{ cursor: "pointer" }}
                                onClick={this.performUnlimitedSearch}
                            >
                                [SHOW ALL]
                            </Typography>
                        </>
                    )}
                </MenuItem>
                <Divider />
                {matchesPerGroup.map(([groupIri, groupItems], index) => (
                    <React.Fragment key={groupIri + "-" + index}>
                        <MenuItem disabled>
                            <Typography variant="subtitle2">{groupItems[0].groupText}</Typography>
                        </MenuItem>
                        {groupItems.map((item, index) => (
                            <MenuItem key={item.iri + "-" + index} onClick={this.handleMenuClose}>
                                <Link
                                    to={"?uri=" + encodeURIComponent(item.iri)}
                                    className="dropdown-item search-result-item"
                                    style={{ textDecoration: "none", color: "inherit" }}
                                >
                                    {item.name}
                                </Link>
                            </MenuItem>
                        ))}
                        {/* Do not render divider for the last item */}
                        {index < matchesPerGroup.length - 1 && <Divider />}
                    </React.Fragment>
                ))}
            </>
        );
    }
}
